import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ADMIN_ENDPOINTS, adminFetch } from '../config';
import './Admin.css';

const POLL_INTERVAL_MS = 3000;

export default function AdminChatConversations() {
  const [searchParams, setSearchParams] = useSearchParams();
  const conversationParam = searchParams.get('conversation');
  const [list, setList] = useState([]);
  const [selectedId, setSelectedId] = useState(() => {
    const id = conversationParam ? parseInt(conversationParam, 10) : null;
    return Number.isFinite(id) ? id : null;
  });
  const [conversation, setConversation] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [showQueue, setShowQueue] = useState(true);
  const [showAgentOnly, setShowAgentOnly] = useState(false);
  const threadRef = useRef(null);
  const lastMessageKeyRef = useRef(null);

  useEffect(() => {
    const id = conversationParam ? parseInt(conversationParam, 10) : null;
    if (Number.isFinite(id)) setSelectedId(id);
  }, [conversationParam]);

  const loadList = () => {
    setLoading(true);
    setError('');
    adminFetch(ADMIN_ENDPOINTS.chatConversationsList)
      .then((r) => r.json())
      .then((data) => {
        if (data.error) setError(data.error);
        else setList(Array.isArray(data) ? data : []);
      })
      .catch(() => setError('Failed to load'))
      .finally(() => setLoading(false));
  };

  const loadConversation = (id, showLoading = true) => {
    if (!id) return;
    if (showLoading) setLoadingDetail(true);
    const url = typeof ADMIN_ENDPOINTS.chatConversationById === 'function'
      ? ADMIN_ENDPOINTS.chatConversationById(id)
      : `${ADMIN_ENDPOINTS.chatConversationById}?id=${id}`;
    adminFetch(url)
      .then((r) => r.json())
      .then((data) => {
        if (data.error) setError(data.error);
        else setConversation(data);
      })
      .catch(() => setError('Failed to load conversation'))
      .finally(() => showLoading && setLoadingDetail(false));
  };

  useEffect(() => {
    loadList();
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setConversation(null);
      return;
    }
    loadConversation(selectedId, true);
  }, [selectedId]);

  // Scroll thread container to bottom when the last message changes
  useLayoutEffect(() => {
    if (!threadRef.current || !conversation?.messages || !conversation.messages.length) return;
    const last = conversation.messages[conversation.messages.length - 1];
    const key = `${last.id ?? ''}-${last.created_at ?? ''}-${last.role}-${(last.content || '').length}`;
    if (lastMessageKeyRef.current === key) return;
    lastMessageKeyRef.current = key;
    const el = threadRef.current;
    el.scrollTop = el.scrollHeight;
  }, [conversation?.messages]);

  // Poll for new messages and list updates when a conversation is open
  useEffect(() => {
    if (!selectedId) return;
    const id = selectedId;
    const conversationUrl = typeof ADMIN_ENDPOINTS.chatConversationById === 'function'
      ? ADMIN_ENDPOINTS.chatConversationById(id)
      : `${ADMIN_ENDPOINTS.chatConversationById}?id=${id}`;
    const tick = () => {
      Promise.all([
        adminFetch(conversationUrl).then((r) => r.json()),
        adminFetch(ADMIN_ENDPOINTS.chatConversationsList).then((r) => r.json()),
      ]).then(([convData, listData]) => {
        if (!convData.error && convData.messages) {
          setConversation((c) => {
            // If we don't have a conversation yet, just take the server one
            if (!c || !c.messages) return convData;
            if (c.conversation_id !== id && c.id !== id) return c;
            const currentLen = c.messages.length || 0;
            const nextLen = convData.messages.length || 0;
            // Never overwrite with fewer messages (stale response)
            if (nextLen < currentLen) return c;
            return { ...c, ...convData };
          });
        }
        if (!listData.error && Array.isArray(listData)) setList(listData);
      }).catch(() => {});
    };
    const interval = setInterval(tick, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [selectedId]);

  const takeOver = async () => {
    if (!selectedId || sending) return;
    setSending(true);
    try {
      const res = await adminFetch(ADMIN_ENDPOINTS.chatReply, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ conversation_id: selectedId, take_over: true }),
      });
      const data = await res.json();
      if (data.error) setError(data.error);
      else {
        setConversation((c) => (c ? { ...c, status: 'live_agent' } : null));
        loadList();
      }
    } catch {
      setError('Failed to take over');
    } finally {
      setSending(false);
    }
  };

  const sendReply = async () => {
    const text = replyText.trim();
    if (!selectedId || !text || sending) return;
    setSending(true);
    try {
      const res = await adminFetch(ADMIN_ENDPOINTS.chatReply, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversation_id: selectedId,
          content: text,
          take_over: conversation?.status !== 'live_agent',
        }),
      });
      const data = await res.json();
      if (data.error) setError(data.error);
      else {
        setReplyText('');
        setConversation((c) => {
          if (!c) return null;
          const newMsg = { id: Date.now(), role: 'agent', content: text, created_at: new Date().toISOString() };
          return { ...c, messages: [...(c.messages || []), newMsg], status: 'live_agent' };
        });
        loadList();
      }
    } catch {
      setError('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  if (loading) return <p>Loading…</p>;
  if (error && !selectedId) return <p style={{ color: 'var(--admin-danger)' }}>{error}</p>;

  const statusRank = {
    awaiting_agent: 0,
    live_agent: 1,
    bot: 2,
    closed: 3,
  };

  const sortedList = [...list].sort((a, b) => {
    const ra = statusRank[a.status] ?? 99;
    const rb = statusRank[b.status] ?? 99;
    if (ra !== rb) return ra - rb;
    const ta = a.updated_at ? new Date(a.updated_at).getTime() : 0;
    const tb = b.updated_at ? new Date(b.updated_at).getTime() : 0;
    return tb - ta; // newest first within same status
  });

  const filteredList = showAgentOnly
    ? sortedList.filter((c) => c.status === 'live_agent' || c.status === 'awaiting_agent')
    : sortedList;

  const totalConversations = list.length;
  const awaitingCount = list.filter((c) => c.status === 'awaiting_agent').length;
  const liveCount = list.filter((c) => c.status === 'live_agent').length;
  const openCount = awaitingCount + liveCount;
  const closedCount = list.filter((c) => c.status === 'closed').length;

  return (
    <>
      <div className="admin-chat-header">
        <h1>Chat conversations</h1>
        <div className="admin-chat-header-actions">
          <label className="admin-chat-filter">
            <input
              type="checkbox"
              checked={showAgentOnly}
              onChange={(e) => setShowAgentOnly(e.target.checked)}
            />
            <span>Agent chats only</span>
          </label>
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={() => setShowQueue((v) => !v)}
          >
            {showQueue ? 'Hide queue' : 'Show queue'}
          </button>
        </div>
      </div>
      <div className="admin-chat-stats">
        <span><strong>{totalConversations}</strong> total</span>
        <span><strong>{openCount}</strong> open (<strong>{awaitingCount}</strong> awaiting, <strong>{liveCount}</strong> live)</span>
        <span><strong>{closedCount}</strong> closed</span>
      </div>
      {error && selectedId && <p style={{ color: 'var(--admin-danger)' }}>{error}</p>}
      <div className="admin-chat-layout">
        {showQueue && (
        <div className="admin-chat-list">
          <ul>
            {filteredList.map((c) => (
              <li
                key={c.id}
                className={selectedId === c.id ? 'active' : ''}
                onClick={() => {
                  setSelectedId(c.id);
                  setSearchParams({ conversation: c.id });
                }}
              >
                <span className="admin-chat-status admin-chat-status--{c.status}">{c.status}</span>
                <span className="admin-chat-meta">
                  {c.contact_name ? c.contact_name : c.visitor_session_id?.slice(0, 8) + '…'} · {c.message_count ?? 0} msgs
                </span>
                <span className="admin-chat-date">
                  {c.updated_at ? new Date(c.updated_at).toLocaleString() : ''}
                </span>
              </li>
            ))}
          </ul>
          {filteredList.length === 0 && <p>No conversations yet.</p>}
        </div>
        )}
        <div className="admin-chat-detail">
          {!selectedId && <p className="admin-chat-placeholder">Select a conversation</p>}
          {selectedId && loadingDetail && <p>Loading…</p>}
          {selectedId && !loadingDetail && conversation && (
            <>
              {(conversation.contact_name || conversation.contact_email) && (
                <div className="admin-chat-contact">
                  {conversation.contact_name && <span className="admin-chat-contact-name">{conversation.contact_name}</span>}
                  {conversation.contact_email && (
                    <a href={`mailto:${encodeURIComponent(conversation.contact_email)}`} className="admin-chat-contact-email">
                      {conversation.contact_email}
                    </a>
                  )}
                </div>
              )}
              <div className="admin-chat-thread" ref={threadRef}>
                {conversation.messages?.map((m) => (
                  <div key={m.id} className={`admin-chat-msg admin-chat-msg--${m.role}`}>
                    <span className="admin-chat-msg-meta">
                      <span className="admin-chat-msg-role">{m.role}</span>
                      {m.created_at && <span className="admin-chat-msg-time">{new Date(m.created_at).toLocaleString()}</span>}
                    </span>
                    <div className="admin-chat-msg-body">{m.content}</div>
                  </div>
                ))}
              </div>
              <div className="admin-chat-reply">
                {conversation.status !== 'live_agent' && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={takeOver}
                    disabled={sending}
                  >
                    Take over (reply as live agent)
                  </button>
                )}
                <div className="admin-chat-reply-row">
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your reply…"
                    rows={3}
                    disabled={sending}
                  />
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={sendReply}
                    disabled={!replyText.trim() || sending}
                  >
                    Send
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
